import { consola } from 'consola'
import { computed, effectScope, ref, watch, type ComputedRef, type EffectScope, type Ref } from 'vue'
import { useBasketState } from './useBasketState'
import {
  collectGiftProductIds,
  computeGiftBase,
  findGiftUnitPrice,
  pickGiftTier,
  type GiftTier,
  type GiftTierSelection,
} from './giftTierLogic'
import { useCartStore } from '@entities/order'
import { useInstitutionStore } from '@entities/institution'
import { useApi } from '@shared/api'

const log = consola.withTag('gift-tiers')

/**
 * Механика «Добери до суммы — получи подарок» на витрине.
 *
 * Отвечает за:
 *  - загрузку активных шагов филиала (GET /average-check/tiers/:branchId);
 *  - расчёт базы порогов (оплаченные товары после скидок, без единицы подарка);
 *  - подсказку «добери ещё N₽» и прогресс;
 *  - авто-добавление/замену подарочного блюда в корзину (isGift + giftSource='tier'),
 *    чтобы касса могла честно применить скидку (подарок обязан лежать в корзине).
 *
 * Взаимоисключение с подарком-промокодом (gift_product): если в корзине есть подарок
 * промокода, шаг-подарок не выдаётся (один подарок за заказ).
 *
 * ⚠️ Состояние и вотчеры живут в ОТДЕЛЬНОМ (detached) effectScope, а не в scope
 * компонента, который первым вызвал композабл. Иначе Vue останавливает их при
 * размонтировании BasketGiftTierNudge (корзина опустела, заказ оформлен, «Очистить
 * корзину») — и до конца сессии подарок больше никогда не добавляется: плашка
 * «… — в подарок!» рисуется (computed пересоздаются на каждый вызов), а товара в
 * корзине нет.
 */

/** Данные подсказки для UI. */
export interface GiftTierNudge {
  hasCurrent: boolean
  currentGiftName: string | null
  hasNext: boolean
  nextGiftName: string | null
  nextThreshold: number | null
  toNext: number
  base: number
}

/** Публичный API механики. */
export interface GiftTiersApi {
  isEnabled: Ref<boolean>
  tiers: Ref<GiftTier[]>
  base: ComputedRef<number>
  selection: ComputedRef<GiftTierSelection>
  nudge: ComputedRef<GiftTierNudge | null>
  /**
   * Скидка за подарок = цена одной единицы подарочного блюда, лежащего в корзине.
   * 0, если подарок не выдан. Зеркало `discountAmount` из `pickGiftForCart` на бэке.
   */
  giftDiscount: ComputedRef<number>
  /**
   * Цена одной единицы подарочного блюда в корзине БЕЗ проверки порога — база
   * промокода (зеркало `findGiftLine` на бэке).
   */
  giftLinePrice: ComputedRef<number>
  /** id позиции, выданной как подарок сейчас (для отрисовки строки корзины). */
  grantedGiftProductId: ComputedRef<string | null>
  loadTiers: (branchId: number) => Promise<void>
}

function createGiftTiers(): GiftTiersApi {
  const api = useApi(true) // v2
  const cartStore = useCartStore()
  const institutionStore = useInstitutionStore()
  const basketState = useBasketState()

  const tiers = ref<GiftTier[]>([])
  const isEnabled = ref(false)
  const loadedBranchId = ref<number | null>(null)

  /**
   * Владение подарком переживает перезагрузку.
   *
   * Сервер не хранит isGift/giftSource: после F5 корзина приезжает с обычной
   * позицией, и механика не может отличить выданный ею подарок от товара, который
   * гость положил сам. Из-за этого при снятии подарка (например, применили
   * промокод-подарок — один подарок за заказ) бывший бесплатный товар оставался в
   * корзине ПЛАТНЫМ.
   *
   * Хранение — localStorage, а НЕ sessionStorage: корзина живёт на сервере и
   * переживает закрытие вкладки, а запись о владении в sessionStorage — нет. Гость
   * получал подарок, возвращался на следующий день, добирал до следующего шага — и
   * прежний подарок оставался в корзине платной строкой, которую он не выбирал.
   */
  const OWNERSHIP_KEY = 'giftTierOwnership'

  function readOwnership(): { productId: string, adopted: boolean } | null {
    if (!process.client) return null
    try {
      const raw = localStorage.getItem(OWNERSHIP_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return typeof parsed?.productId === 'string' ? { productId: parsed.productId, adopted: !!parsed.adopted } : null
    } catch {
      return null
    }
  }

  function writeOwnership(productId: string | null, adopted: boolean): void {
    if (!process.client) return
    if (productId == null) localStorage.removeItem(OWNERSHIP_KEY)
    else localStorage.setItem(OWNERSHIP_KEY, JSON.stringify({ productId, adopted }))
  }

  const restored = readOwnership()

  /** id подарка, которым владеет ЭТА механика (для замены/снятия). */
  const tierGiftProductId = ref<string | null>(restored?.productId ?? null)
  /**
   * Подарок не добавлен нами, а «усыновлён»: позиция уже лежала в корзине.
   * Такую при снятии подарка НЕ удаляем, а только снимаем пометку — иначе механика
   * унесла бы товар, который пользователь положил сам.
   */
  const tierGiftAdopted = ref(restored?.adopted ?? false)

  const currentBranchId = computed(() => Number(institutionStore.selectedBranch?.id) || null)

  /** Ищет товар (single или вариант group) в меню заведения по id. */
  function findGiftProduct(productId: string): any {
    const id = Number(productId)
    if (!id || Number.isNaN(id)) return null
    for (const category of institutionStore.products || []) {
      if (!category.products) continue
      for (const product of category.products) {
        if (product.type === 'single' && Number(product.id) === id) return product
        if (product.type === 'group' && product.variants) {
          const variant = product.variants.find((v: any) => Number(v.id) === id)
          if (variant) return variant
        }
      }
    }
    return null
  }

  /** Первый доступный (в меню и не на стопе) подарок из цепочки шага. */
  function resolveGiftProduct(tier: GiftTier): any {
    for (const giftId of tier.giftProductIds) {
      const product = findGiftProduct(giftId)
      if (product && product.stop_list !== true) return product
    }
    return null
  }

  /** Активен ли подарок-промокода в корзине (для взаимоисключения). */
  const promocodeGiftActive = computed(() =>
    cartStore.cart.some(
      (item: any) => item.isGift === true && item.giftSource !== 'tier',
    ),
  )

  /** Только шаги с доступным подарком. */
  const availableTiers = computed(() =>
    tiers.value.filter(tier => resolveGiftProduct(tier) !== null),
  )

  /** id всех подарков активных шагов — по ним узнаём подарочную позицию в корзине. */
  const giftProductIds = computed(() => collectGiftProductIds(availableTiers.value))

  /** База порогов: корзина − скидки − одна единица подарка (если он уже в корзине). */
  const base = computed(() => {
    const pointsUsed = basketState.useLoyaltyPoints ? basketState.pointsToUse : 0
    return computeGiftBase(
      cartStore.cart as any[],
      basketState.promocodeDiscount,
      pointsUsed,
      giftProductIds.value,
    )
  })

  /**
   * Цена ОДНОЙ единицы подарочного блюда, лежащего в корзине, БЕЗ проверки порога.
   *
   * Ровно то же, что `GiftTierResolverService.findGiftLine` на бэкенде: эта величина
   * нужна ДО того, как посчитаны скидки (база промокода), поэтому порог тут проверять
   * нельзя — вышел бы цикл «промокод → порог → промокод».
   */
  const giftLinePrice = computed(() =>
    isEnabled.value ? findGiftUnitPrice(cartStore.cart as any[], giftProductIds.value) : 0,
  )

  /** Достигнутый и следующий шаг. */
  const selection = computed(() => pickGiftTier(availableTiers.value, base.value))

  /**
   * Скидка за выданный подарок: цена ОДНОЙ единицы подарочного блюда в корзине.
   *
   * Подарок лежит в корзине по полной цене (так его видит касса), поэтому эта величина
   * обязана вычитаться из суммы заказа и из базы сервисного сбора — ровно как это делает
   * `pickGiftForCart` → `computeOrderPricing` на бэкенде. Иначе гость платит за подарок.
   *
   * Считаем по позиции В КОРЗИНЕ, а не по цене из меню: если подарок «усыновлён»
   * (пользователь сам положил это блюдо), бесплатна всё равно ровно одна единица.
   *
   * Позиция корзины, которая СЕЙЧАС выдана как подарок за сумму (или null).
   *
   * Источник истины для отрисовки строки. Флаг `isGift` на позиции для этого не годится:
   * его ставит механика, а корзина у авторизованного приезжает с сервера, который
   * `isGift`/`giftSource` не хранит. Любая перезагрузка списка затирает пометку — строка
   * рисуется обычной платной, хотя скидка за подарок применена.
   */
  const grantedGiftLine = computed<{ productId: string, price: number } | null>(() => {
    if (!isEnabled.value || promocodeGiftActive.value) return null

    const current = selection.value.current
    if (!current) return null

    const desired = resolveGiftProduct(current)
    if (!desired) return null

    const line: any = cartStore.cart.find((item: any) => String(item.id) === String(desired.id))
    if (!line) return null

    return { productId: String(desired.id), price: Math.max(0, Number(line.price) || 0) }
  })

  /** id подарочной позиции для UI (null, если подарок сейчас не выдан). */
  const grantedGiftProductId = computed(() => grantedGiftLine.value?.productId ?? null)

  const giftDiscount = computed(() => {
    return grantedGiftLine.value?.price ?? 0
  })

  /** Подсказка для UI. */
  const nudge = computed<GiftTierNudge | null>(() => {
    if (!isEnabled.value || availableTiers.value.length === 0) return null
    if (promocodeGiftActive.value) return null

    const { current, next } = selection.value
    const currentGift = current ? resolveGiftProduct(current) : null
    const nextGift = next ? resolveGiftProduct(next) : null

    return {
      hasCurrent: Boolean(current),
      currentGiftName: currentGift?.name ?? null,
      hasNext: Boolean(next),
      nextGiftName: nextGift?.name ?? null,
      nextThreshold: next?.threshold ?? null,
      toNext: next ? Math.max(0, next.threshold - base.value) : 0,
      base: base.value,
    }
  })

  /** Загрузка активных шагов филиала. */
  async function loadTiers(branchId: number): Promise<void> {
    if (!branchId) return
    try {
      const { data } = await api.get<{ isEnabled: boolean, tiers: GiftTier[] }>(
        `/average-check/tiers/${branchId}`,
      )
      isEnabled.value = data?.isEnabled ?? false
      tiers.value = Array.isArray(data?.tiers) ? data.tiers : []
      loadedBranchId.value = branchId
      log.debug('шаги загружены', { branchId, count: tiers.value.length, isEnabled: isEnabled.value })
    } catch (error) {
      isEnabled.value = false
      tiers.value = []
      log.warn('не удалось загрузить шаги подарков', error)
    }
  }

  /** Отпускает подарок: наш — удаляем из корзины, усыновлённый — только разпомечаем. */
  async function releaseTierGift(): Promise<void> {
    const giftId = tierGiftProductId.value
    const adopted = tierGiftAdopted.value
    tierGiftProductId.value = null
    tierGiftAdopted.value = false
    if (!giftId) {
      writeOwnership(null, false)
      return
    }

    // После перезагрузки giftSource теряется, а владение мы помним сами — иначе
    // наш же подарок остался бы в корзине платной позицией.
    const item: any = cartStore.cart.find((i: any) => String(i.id) === giftId)
    if (!item) {
      // Запись о владении снимаем, только если корзина ТОЧНО без подарка: непустая
      // корзина — это загруженная корзина, и раз подарка в ней нет, гость удалил его сам.
      //
      // Пустая корзина не доказывает ничего: на старте она пуста, пока не приехала с
      // сервера, а шаги подарков грузятся параллельно и часто выигрывают гонку. Снять
      // владение здесь — значит принять собственный подарок за товар гостя, когда
      // корзина всё-таки приедет, и при смене шага оставить его платной строкой.
      if (cartStore.cart.length > 0) writeOwnership(null, false)
      return
    }

    writeOwnership(null, false)

    if (adopted) {
      item.isGift = false
      item.giftSource = undefined
      log.debug('подарок разпомечен (позиция была в корзине до нас)', giftId)
      return
    }

    const branchId = currentBranchId.value
    if (!branchId) return

    const addons = (item.addons ?? []).map(Number)
    const excludes = (item.excludes ?? []).map(Number)
    const quantity = Number(item.quantity) || 1

    // Нашей была ровно ОДНА единица. Если гость доложил ещё, снимаем только её —
    // раньше удалялась вся позиция, и вместе с подарком из корзины исчезал товар,
    // за который гость платит.
    if (quantity > 1) {
      item.isGift = false
      item.giftSource = undefined
      await cartStore.updateQuantity(item.id, quantity - 1, branchId, addons, excludes)
      log.debug('подарок снят, платные единицы оставлены', giftId, quantity - 1)
      return
    }

    await cartStore.removeFromCart(item.id, branchId, addons, excludes)
    log.debug('подарок снят', giftId)
  }

  /** Приводит корзину к нужному подарку: добавляет / усыновляет / меняет / снимает. */
  async function syncGift(): Promise<void> {
    if (!isEnabled.value) return

    // Корзина ещё переезжает на другой филиал: её позиции пока с прежними id.
    // Не дождавшись, мы не нашли бы подарок под новым id и выдали ЕЩЁ один —
    // за каждое переключение филиала количество подарка росло на единицу.
    // Вотчер ниже перезапустит синхронизацию, когда переезд закончится.
    if (!cartStore.isSettledForBranch) {
      log.debug('Подарок за сумму: ждём, пока корзина догонит филиал')
      return
    }

    // Взаимоисключение: активен подарок-промокода → шаг-подарок не выдаём.
    if (promocodeGiftActive.value) {
      if (tierGiftProductId.value) await releaseTierGift()
      return
    }

    const { current } = selection.value
    const desiredProduct = current ? resolveGiftProduct(current) : null
    const desiredId = desiredProduct ? String(desiredProduct.id) : null

    // Порог упал или сменился шаг — отпускаем прежний подарок.
    if (tierGiftProductId.value && tierGiftProductId.value !== desiredId) {
      await releaseTierGift()
    }

    if (!desiredId || !desiredProduct) return

    const branchId = currentBranchId.value
    if (!branchId) return

    // Подарочный товар уже в корзине → вторую единицу НЕ добавляем.
    // Так бывает после перезагрузки (сервер не хранит isGift/giftSource) и когда
    // пользователь сам положил это блюдо: касса всё равно делает бесплатной одну единицу.
    const existing: any = cartStore.cart.find((i: any) => String(i.id) === desiredId)
    if (existing) {
      if (existing.giftSource !== 'tier') {
        // Владение помним между перезагрузками: если эту позицию выдали мы, она
        // остаётся нашей и при снятии удаляется, а не превращается в платную.
        //
        // Читаем запись СЕЙЧАС, а не снимок времени создания композабла: гость мог
        // доложить ещё одну единицу подарочного блюда уже после выдачи — тогда с
        // позиции слетает giftSource, мы приходим сюда снова и по устаревшему снимку
        // считали собственный подарок «чужим». При снятии он оставался в корзине
        // платным, то есть гость платил за то, что мы сами ему положили.
        const ownership = readOwnership()
        const owned = ownership?.productId === desiredId && ownership.adopted === false
        existing.isGift = true
        existing.giftSource = 'tier'
        tierGiftAdopted.value = !owned
        log.debug(
          owned ? '🎁 подарок восстановлен после перезагрузки' : '🎁 подарок усыновлён из корзины',
          desiredProduct.name,
        )
      }
      tierGiftProductId.value = desiredId
      writeOwnership(desiredId, tierGiftAdopted.value)
      return
    }

    await cartStore.addToCart(
      {
        id: String(desiredProduct.id),
        name: desiredProduct.name,
        price: Number(desiredProduct.price) || 0,
        img: desiredProduct.img || [],
        category_id: desiredProduct.category_id,
        itemType: 'product',
        productId: String(desiredProduct.id),
        isGift: true,
        giftSource: 'tier',
        quantity: 1,
      } as any,
      branchId,
    )
    tierGiftProductId.value = desiredId
    tierGiftAdopted.value = false
    writeOwnership(desiredId, false)
    log.success('🎁 подарок за сумму добавлен', desiredProduct.name)
  }

  // Перезагрузка шагов при смене филиала.
  watch(
    currentBranchId,
    (branchId) => {
      if (branchId && branchId !== loadedBranchId.value) void loadTiers(branchId)
    },
    { immediate: true },
  )

  // Авто-синхронизация подарка при изменении базы/шагов/промо-подарка.
  // База не меняется от самой выдачи подарка (см. computeGiftBase) → без циклов.
  // immediate — чтобы состояние сверялось и когда корзина уже подъехала с сервера
  // и больше ничего меняться не будет (иначе подарок не выдавался бы до первого
  // изменения количества).
  watch(
    () => ({
      base: base.value,
      tierIds: availableTiers.value.map(t => t.id).join(','),
      promo: promocodeGiftActive.value,
      enabled: isEnabled.value,
      // как только корзина догонит филиал — синхронизируемся повторно
      settled: cartStore.isSettledForBranch,
    }),
    () => {
      void syncGift()
    },
    { immediate: true },
  )

  // Скидка за подарок живёт в состоянии корзины: её потребляют расчёт сервисного сбора,
  // итог к оплате и детализация цен. Синхронизируем в том же detached scope, что и сама
  // механика, — иначе значение «застрянет» при размонтировании компонента корзины.
  watch(
    giftDiscount,
    (discount) => {
      basketState.setGiftTierDiscount(discount)
    },
    { immediate: true },
  )

  return {
    isEnabled,
    tiers,
    base,
    selection,
    nudge,
    giftDiscount,
    giftLinePrice,
    grantedGiftProductId,
    loadTiers,
  }
}

/** Инертная заглушка для SSR: на сервере механика не работает и состояние не шарится. */
function createInertGiftTiers(): GiftTiersApi {
  return {
    isEnabled: ref(false),
    tiers: ref<GiftTier[]>([]),
    base: computed(() => 0),
    selection: computed(() => ({ current: null, next: null })),
    nudge: computed(() => null),
    giftDiscount: computed(() => 0),
    giftLinePrice: computed(() => 0),
    grantedGiftProductId: computed(() => null),
    loadTiers: async () => {},
  }
}

// Синглтон в отдельном scope: живёт независимо от жизненного цикла компонентов,
// поэтому мобильная и десктопная корзины делят один экземпляр и не дублируют подарок.
let scope: EffectScope | null = null
let instance: GiftTiersApi | null = null

export function useGiftTiers(): GiftTiersApi {
  if (!process.client) return createInertGiftTiers()
  if (instance) return instance
  scope = effectScope(true)
  instance = scope.run(() => createGiftTiers()) as GiftTiersApi
  return instance
}

/** Только для тестов: сбрасывает синглтон между кейсами. */
export function __resetGiftTiersForTests(): void {
  scope?.stop()
  scope = null
  instance = null
}
